import type {
  ProjectCheckoutDeletionExecuteInput,
  ProjectCheckoutDeletionPreview,
  ProjectCheckoutDeletionResult,
} from "@cmmd-forge/contracts";

export interface ProjectGroupRemovalTarget {
  readonly environmentId: string;
  readonly projectId: string;
  readonly label: string;
  readonly path: string;
  readonly threadCount: number;
  readonly kind: "local" | "cloud";
}

export interface ProjectGroupRemovalResult {
  readonly target: ProjectGroupRemovalTarget;
  readonly fileDeletion: "not-requested" | "not-attempted" | "deleted" | "blocked" | "failed";
  readonly metadata: "pending" | "removed" | "failed";
  readonly message?: string;
}

interface ProjectGroupRemovalRunnerInput {
  readonly targets: readonly ProjectGroupRemovalTarget[];
  readonly deleteFiles: boolean;
  readonly confirmation: string;
  readonly preview: (target: ProjectGroupRemovalTarget) => Promise<ProjectCheckoutDeletionPreview>;
  readonly deleteFilesForCheckout: (
    target: ProjectGroupRemovalTarget,
    input: ProjectCheckoutDeletionExecuteInput,
  ) => Promise<{
    readonly deletion: ProjectCheckoutDeletionResult;
    readonly metadataRemoved: boolean;
  }>;
  readonly removeMetadata: (target: ProjectGroupRemovalTarget) => Promise<void>;
}

const DIRTY_WORK_CONFIRMATION = "delete uncommitted work";

type SettledPreview = PromiseSettledResult<{
  readonly target: ProjectGroupRemovalTarget;
  readonly preview: ProjectCheckoutDeletionPreview;
}>;

function isPreviewExecutable(preview: ProjectCheckoutDeletionPreview): boolean {
  const onlyDirtyBlocker =
    preview.blockers.length === 1 && preview.blockers[0] === "dirty_git_state";
  return (
    preview.method !== "unavailable" &&
    preview.fingerprint !== null &&
    (preview.blockers.length === 0 || onlyDirtyBlocker)
  );
}

function canDeleteDirtyCheckout(
  preview: ProjectCheckoutDeletionPreview,
  confirmation: string,
): boolean {
  return !preview.dirty || confirmation.trim() === DIRTY_WORK_CONFIRMATION;
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

function initialResults(input: ProjectGroupRemovalRunnerInput): ProjectGroupRemovalResult[] {
  return input.targets.map((target) => ({
    target,
    fileDeletion: input.deleteFiles ? "not-attempted" : "not-requested",
    metadata: "pending",
  }));
}

async function collectPreviews(
  input: ProjectGroupRemovalRunnerInput,
): Promise<readonly SettledPreview[]> {
  return Promise.allSettled(
    input.targets.map(async (target) => ({ target, preview: await input.preview(target) })),
  );
}

function previewFailureMessage(entry: SettledPreview, confirmation: string): string | null {
  if (entry.status === "rejected") return errorMessage(entry.reason);
  if (!isPreviewExecutable(entry.value.preview)) {
    return entry.value.preview.blockers.join(" ") || "Checkout files cannot be deleted.";
  }
  return canDeleteDirtyCheckout(entry.value.preview, confirmation)
    ? null
    : `Type ${DIRTY_WORK_CONFIRMATION} to delete uncommitted work.`;
}

function applyPreflight(
  results: ProjectGroupRemovalResult[],
  previews: readonly SettledPreview[],
  confirmation: string,
): boolean {
  let passed = true;
  for (const [index, entry] of previews.entries()) {
    const result = results[index];
    if (!result) continue;
    const message = previewFailureMessage(entry, confirmation);
    if (!message) continue;
    passed = false;
    results[index] = { ...result, fileDeletion: "blocked", message };
  }
  return passed;
}

async function deleteFiles(
  results: ProjectGroupRemovalResult[],
  previews: readonly SettledPreview[],
  execute: ProjectGroupRemovalRunnerInput["deleteFilesForCheckout"],
): Promise<void> {
  for (const [index, entry] of previews.entries()) {
    const result = results[index];
    if (!result || entry.status !== "fulfilled") continue;
    const { preview } = entry.value;
    if (preview.fingerprint === null) continue;
    try {
      const execution = await execute(entry.value.target, {
        projectId: preview.projectId,
        expectedFingerprint: preview.fingerprint,
        forceDirty: preview.dirty,
      });
      results[index] = applyFileDeletionExecution(result, execution);
      if (results[index]?.fileDeletion !== "deleted") break;
    } catch (error) {
      results[index] = { ...result, fileDeletion: "failed", message: errorMessage(error) };
      break;
    }
  }
}

function applyFileDeletionExecution(
  result: ProjectGroupRemovalResult,
  execution: Awaited<ReturnType<ProjectGroupRemovalRunnerInput["deleteFilesForCheckout"]>>,
): ProjectGroupRemovalResult {
  const { deletion } = execution;
  const message = deletion.blockers.join(" ");
  const deleted = deletion.status === "deleted";
  return {
    ...result,
    fileDeletion: deleted ? "deleted" : "blocked",
    metadata: deleted && execution.metadataRemoved ? "removed" : result.metadata,
    ...(message ? { message } : {}),
  };
}

async function removeMetadata(
  results: ProjectGroupRemovalResult[],
  input: ProjectGroupRemovalRunnerInput,
): Promise<void> {
  for (const [index, result] of results.entries()) {
    if (result.metadata === "removed") continue;
    if (input.deleteFiles && result.fileDeletion !== "deleted") continue;
    try {
      await input.removeMetadata(result.target);
      results[index] = { ...result, metadata: "removed" };
    } catch (error) {
      results[index] = { ...result, metadata: "failed", message: errorMessage(error) };
    }
  }
}

export async function runProjectGroupRemoval(
  input: ProjectGroupRemovalRunnerInput,
): Promise<readonly ProjectGroupRemovalResult[]> {
  const results = initialResults(input);

  if (input.deleteFiles) {
    const previews = await collectPreviews(input);
    if (!applyPreflight(results, previews, input.confirmation)) return results;
    await deleteFiles(results, previews, input.deleteFilesForCheckout);
  }
  await removeMetadata(results, input);
  return results;
}
